// Game controllers (Xbox, PlayStation and other pads) through the Gamepad API,
// which every current browser has on desktop and mobile: Chrome, Edge, Firefox
// and Safari, on Windows, macOS, Linux, Android and iOS. The left stick steers,
// the triggers are analog throttle and brake, and the face buttons and D-pad
// drive the menus.
//
// A controller drives the assisted car (like tilt and the keyboard), not sim
// mode: speed-sensitive steering and the steering slew limit are what make a
// thumbstick drivable. Steering wheels are left to WheelManager.
//
// Browsers only reveal a controller once a button is pressed on it, so a
// connected controller means the player picked it up.

import { param } from '../config/params';
import { looksLikeWheel } from '../wheel/gamepadSource';
import { shapeInput } from './steering';

export const controllerParams = {
  deadZone: param({ id: 'pad.deadZone', label: 'Stick dead zone', group: 'Controller', min: 0, max: 0.3, step: 0.01, value: 0.1 }),
  curve: param({ id: 'pad.curve', label: 'Stick response curve (exp)', group: 'Controller', min: 1, max: 3, step: 0.1, value: 1.6 }),
  triggerDeadZone: param({ id: 'pad.triggerDeadZone', label: 'Trigger dead zone', group: 'Controller', min: 0, max: 0.2, step: 0.01, value: 0.04 }),
};

export type ControllerKind = 'xbox' | 'playstation' | 'generic';

/**
 * What a controller button does. Where it overlaps the wheel's actions it uses
 * the same name (select, pause, box, reset, camera, upshift, downshift); back,
 * prev and next are menu-only.
 */
export type ControllerAction = 'select' | 'back' | 'pause' | 'prev' | 'next' | 'upshift' | 'downshift' | 'box' | 'reset' | 'camera';

/**
 * Button layout of the W3C "standard" mapping, which browsers give Xbox,
 * DualShock 4 and DualSense pads on every platform: 0-3 face (A/✕, B/○, X/□,
 * Y/△), 4/5 bumpers, 6/7 triggers (analog `value`), 8 View/Share, 9 Menu/Options,
 * 12-15 D-pad up/down/left/right.
 */
const BUTTON_ACTIONS: ReadonlyArray<[number, ControllerAction]> = [
  [0, 'select'],
  [1, 'back'],
  [2, 'box'],
  [3, 'reset'],
  [4, 'downshift'],
  [5, 'upshift'],
  [8, 'camera'],
  [9, 'pause'],
  [12, 'prev'],
  [13, 'next'],
  [14, 'prev'],
  [15, 'next'],
];
const BRAKE_BUTTON = 6;
const THROTTLE_BUTTON = 7;
/** Non-standard pads (Linux evdev order): left trigger is axis 2, right trigger axis 5, both resting at −1. */
const BRAKE_AXIS = 2;
const THROTTLE_AXIS = 5;

/** Button names per controller family, for on-screen hints. */
export interface ControllerLabels {
  select: string;
  back: string;
  pause: string;
  box: string;
  reset: string;
  camera: string;
  throttle: string;
  brake: string;
}

export const CONTROLLER_LABELS: Record<ControllerKind, ControllerLabels> = {
  xbox: { select: 'A', back: 'B', pause: 'Menu', box: 'X', reset: 'Y', camera: 'View', throttle: 'RT', brake: 'LT' },
  playstation: { select: '✕', back: '○', pause: 'Options', box: '□', reset: '△', camera: 'Share', throttle: 'R2', brake: 'L2' },
  generic: { select: 'A', back: 'B', pause: 'Start', box: 'X', reset: 'Y', camera: 'Select', throttle: 'RT', brake: 'LT' },
};

/** The parts of the Gamepad API's Gamepad this module reads (structural, so tests can fake it). */
export interface PadLike {
  readonly id: string;
  readonly index: number;
  readonly connected: boolean;
  readonly mapping: string;
  readonly axes: readonly number[];
  readonly buttons: ReadonlyArray<{ readonly pressed: boolean; readonly value: number }>;
  readonly vibrationActuator?: HapticLike | null;
}

export interface HapticLike {
  /** Newer browsers list the effects the pad can play; older Chrome had a single `type`. */
  readonly effects?: readonly string[];
  readonly type?: string;
  playEffect(type: 'dual-rumble' | 'trigger-rumble', params: GamepadEffectParameters): Promise<unknown>;
  reset?(): Promise<unknown>;
}

export function controllerKind(id: string): ControllerKind {
  if (/xbox|xinput|045e/i.test(id)) return 'xbox';
  if (/054c|dualsense|dualshock|playstation|wireless controller/i.test(id)) return 'playstation';
  return 'generic';
}

/** A short display name: browsers append vendor/product details in brackets (Chrome) or as a prefix (Firefox). */
export function controllerName(id: string): string {
  const kind = controllerKind(id);
  if (kind === 'xbox') return 'Xbox controller';
  if (kind === 'playstation') return /dualsense/i.test(id) ? 'DualSense controller' : 'PlayStation controller';
  return id.replace(/\s*\(.*?\)\s*/g, ' ').replace(/^[0-9a-f]{1,4}-[0-9a-f]{1,4}-/i, '').trim() || 'Game controller';
}

/**
 * True for a pad we drive with: not a steering wheel (WheelManager has those),
 * and with enough controls to be a game controller rather than, say, a laptop
 * accelerometer that Linux exposes as a joystick.
 */
export function isController(pad: PadLike): boolean {
  if (looksLikeWheel(pad.id)) return false;
  if (pad.mapping === 'standard') return pad.buttons.length >= 10;
  return pad.axes.length >= 2 && pad.buttons.length >= 6;
}

export interface ControllerState {
  /** −1..1, + right, after dead zone and response curve. */
  steer: number;
  throttle: number;
  brake: number;
  /** Buttons pressed since the last read. */
  pressed: ControllerAction[];
}

function triggerValue(v: number, deadZone: number): number {
  if (!(v > deadZone)) return 0;
  return Math.min(1, (v - deadZone) / (1 - deadZone));
}

/** Reads one pad: steering shaping, triggers and button edges. */
export class PadReader {
  private prev: boolean[] = [];
  /** Non-standard pads: trigger axes count once they've been seen at rest (−1), so a centred axis isn't half throttle. */
  private readonly armedAxes = new Set<number>();

  read(pad: PadLike): ControllerState {
    const pressed: ControllerAction[] = [];
    const down = pad.buttons.map((b) => b.pressed);
    for (const [index, action] of BUTTON_ACTIONS) {
      if (down[index] && !this.prev[index] && !pressed.includes(action)) pressed.push(action);
    }
    this.prev = down;
    const dz = controllerParams.triggerDeadZone.value;
    return {
      steer: shapeInput(pad.axes[0] ?? 0, controllerParams.deadZone.value, controllerParams.curve.value),
      throttle: triggerValue(this.trigger(pad, THROTTLE_BUTTON, THROTTLE_AXIS), dz),
      brake: triggerValue(this.trigger(pad, BRAKE_BUTTON, BRAKE_AXIS), dz),
      pressed,
    };
  }

  private trigger(pad: PadLike, button: number, axis: number): number {
    const b = pad.buttons[button];
    if (pad.mapping === 'standard') return b ? b.value || (b.pressed ? 1 : 0) : 0;
    // Non-standard: an analog button if it reports one, else the evdev trigger axis.
    if (b && b.value > 0) return b.value;
    const a = pad.axes[axis];
    if (a === undefined) return b?.pressed ? 1 : 0;
    if (a <= -0.9) this.armedAxes.add(axis);
    return this.armedAxes.has(axis) ? (a + 1) / 2 : b?.pressed ? 1 : 0;
  }
}

export interface RumbleInputs {
  speed: number;
  onKerb: boolean;
  /** Grass, gravel or off the edge. */
  offTrack: boolean;
  /** Speed lost to a barrier this frame, m/s. */
  impact: number;
  frontLock: number;
  rearSpin: number;
  /** 0..1 share of the brake request anti-lock is holding back. */
  absCut: number;
  /** 0..1 share of the drive torque traction control is cutting. */
  tcCut: number;
}

export interface RumbleLevels {
  strong: number;
  weak: number;
  leftTrigger: number;
  rightTrigger: number;
}

/**
 * Controller rumble from what the car is doing: kerbs and rough ground on the
 * light motor, impacts on the heavy one. On Xbox pads with impulse triggers,
 * the brake trigger buzzes when anti-lock is holding the brakes back (or the
 * fronts lock) and the throttle trigger when traction control is cutting power
 * (or the rears spin): the cue to ease off. Pads without trigger motors get a
 * lighter version of the same on the light motor.
 */
export class ControllerRumble {
  private impulse = 0;
  private lastSent = 0;
  /** The last levels() asked for trigger rumble. */
  private triggers = false;
  private playing = false;

  /** Motor levels for this frame (pure apart from the impact decay; exposed for tests). */
  levels(i: RumbleInputs, dt: number): RumbleLevels {
    this.impulse = Math.max(this.impulse * Math.exp(-dt / 0.15), Math.min(1, i.impact / 12));
    const fast = Math.min(1, i.speed / 60);
    const rough = i.speed < 3 ? 0 : i.onKerb ? 0.25 + 0.35 * fast : i.offTrack ? 0.1 + 0.2 * fast : 0;
    // Anti-lock: the assisted car keeps 5% in hand braking in a straight line (a light buzz at
    // the threshold) and cuts 40-90% when braking into a turn (full buzz). Traction control cuts
    // 60-90% of the torque on most exits below 150 km/h, so it stays a gentle, proportional cue.
    const abs = i.speed < 3 ? 0 : Math.min(1, Math.max(0, (i.absCut - 0.02) / 0.1));
    const tc = i.speed < 1 ? 0 : Math.max(0, (i.tcCut - 0.03) / 0.97);
    const left = Math.max(0.6 * i.frontLock, 0.45 * abs);
    const right = Math.max(0.5 * i.rearSpin, 0.3 * tc);
    this.triggers = left > 0.02 || right > 0.02;
    return {
      strong: Math.min(1, this.impulse + 0.3 * i.rearSpin),
      weak: Math.min(1, rough + 0.4 * i.frontLock),
      leftTrigger: Math.min(1, left),
      rightTrigger: Math.min(1, right),
    };
  }

  /** Per frame while driving. Effects are re-sent every ~80 ms with a little overlap, so they stop by themselves if the game does. */
  update(pad: PadLike | undefined, i: RumbleInputs, dt: number, now = performance.now()): void {
    const l = this.levels(i, dt);
    const act = pad?.vibrationActuator;
    if (!act) return;
    const on = l.strong > 0.02 || l.weak > 0.02 || l.leftTrigger > 0.02 || l.rightTrigger > 0.02;
    if (!on) {
      if (this.playing) this.stop(pad);
      return;
    }
    if (now - this.lastSent < 80) return;
    this.lastSent = now;
    this.playing = true;
    const effects = act.effects ?? (act.type ? [act.type] : ['dual-rumble']);
    const params = { duration: 120, startDelay: 0, strongMagnitude: l.strong, weakMagnitude: l.weak };
    const triggerMotors = effects.includes('trigger-rumble');
    // No trigger motors: fold the trigger cues into the light motor, at half the strength.
    const folded = this.triggers && !triggerMotors ? Math.min(1, l.weak + Math.max(l.leftTrigger, l.rightTrigger) / 2) : l.weak;
    const play = triggerMotors
      ? act.playEffect('trigger-rumble', { ...params, leftTrigger: l.leftTrigger, rightTrigger: l.rightTrigger })
      : effects.includes('dual-rumble')
        ? act.playEffect('dual-rumble', { ...params, weakMagnitude: folded })
        : undefined;
    void play?.catch(() => undefined);
  }

  /** Stop any rumble now (pause, menus, vibration turned off). */
  stop(pad: PadLike | undefined): void {
    if (!this.playing) return;
    this.playing = false;
    this.impulse = 0;
    void pad?.vibrationActuator?.reset?.()?.catch(() => undefined);
  }
}

/**
 * Finds and reads the player's controller. With several connected, the one
 * last touched is used.
 */
export class ControllerManager {
  readonly rumble = new ControllerRumble();
  /** Latest state (updated by poll()). */
  state: ControllerState | undefined;
  /** Called when a controller connects or disconnects. */
  onChange: (() => void) | undefined;
  private index: number | undefined;
  private id = '';
  private readonly readers = new Map<number, PadReader>();

  constructor(private readonly pads: () => ReadonlyArray<PadLike | null> = () => (navigator.getGamepads?.() ?? []) as ReadonlyArray<PadLike | null>) {}

  init(): void {
    if (typeof window === 'undefined') return;
    // Only pick the pad here; buttons are read in poll() so no press is lost between frames.
    window.addEventListener('gamepadconnected', () => this.select());
    window.addEventListener('gamepaddisconnected', () => this.select());
  }

  get connected(): boolean {
    return this.index !== undefined;
  }

  get kind(): ControllerKind {
    return controllerKind(this.id);
  }

  get name(): string {
    return controllerName(this.id);
  }

  get labels(): ControllerLabels {
    return CONTROLLER_LABELS[this.kind];
  }

  /** The pad in use, for rumble. */
  get pad(): PadLike | undefined {
    return this.list().find((p) => p.index === this.index && p.id === this.id);
  }

  /** Read the controller (call once per frame). */
  poll(): ControllerState | undefined {
    const pads = this.select();
    this.state = undefined;
    for (const p of pads) {
      // Every pad keeps its button history fresh, so switching pads doesn't replay a held button.
      let reader = this.readers.get(p.index);
      if (!reader) this.readers.set(p.index, (reader = new PadReader()));
      const s = reader.read(p);
      if (p.index === this.index) this.state = s;
    }
    return this.state;
  }

  private list(): PadLike[] {
    let all: ReadonlyArray<PadLike | null>;
    try {
      all = this.pads();
    } catch {
      return []; // Permissions policy can block the Gamepad API (e.g. inside an iframe).
    }
    return all.filter((p): p is PadLike => !!p && p.connected && isController(p));
  }

  /** Pick the pad to use: the current one, unless another is being used and it isn't. */
  private select(): PadLike[] {
    const pads = this.list();
    let current = pads.find((p) => p.index === this.index && p.id === this.id);
    for (const p of pads) {
      if (p !== current && (!current || (busy(p) && !busy(current)))) current = p;
    }
    for (const i of this.readers.keys()) if (!pads.some((p) => p.index === i)) this.readers.delete(i);
    const changed = (current?.index ?? -1) !== (this.index ?? -1) || (current?.id ?? '') !== this.id;
    this.index = current?.index;
    this.id = current?.id ?? '';
    if (changed) this.onChange?.();
    return pads;
  }
}

/** A pad with a button held or the left stick well off centre. */
function busy(p: PadLike): boolean {
  return p.buttons.some((b) => b.pressed) || Math.abs(p.axes[0] ?? 0) > 0.5 || Math.abs(p.axes[1] ?? 0) > 0.5;
}
